import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon, CircleDashedIcon, MinusCircleIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { Select } from '../../components/ui/Field';
import { Meter } from '../../components/ui/Meter';
import { FeatureStatusBadge, dependencyLabel } from '../../components/platform/FeatureStatusBadge';
import { featureRegister, gapAudit, readinessGroups } from '../../data/platform';
import type { GapClass, ReadinessState } from '../../types/platform';
const TABS = ['Feature status', 'Launch checklist', 'Gap audit', 'Environments'] as const;
type Tab = (typeof TABS)[number];
const STATE_META: Record<ReadinessState, {
  label: string;
  icon: React.ElementType;
  className: string;
}> = {
  complete: {
    label: 'Complete',
    icon: CheckCircle2Icon,
    className: 'text-ok-700'
  },
  partial: {
    label: 'Partial',
    icon: CircleDashedIcon,
    className: 'text-warn-700'
  },
  blocked: {
    label: 'Blocked',
    icon: AlertTriangleIcon,
    className: 'text-danger-700'
  },
  not_started: {
    label: 'Not started',
    icon: MinusCircleIcon,
    className: 'text-ink-muted'
  }
};
const GAP_LABELS: Record<GapClass, string> = {
  complete: 'Fully implemented',
  partial: 'Partially implemented',
  interface_only: 'Interface only',
  needs_provider: 'Needs an external provider',
  needs_configuration: 'Needs configuration',
  needs_approval: 'Needs regulatory or legal approval'
};

/**
 * The production-readiness console.
 *
 * This is the audit the transition specification asks for, kept as a live
 * surface rather than a document: it reads the same feature register the
 * product reads, so a capability cannot be described as live here while it is
 * gated in the product, or the reverse.
 */
export function ProductionReadiness() {
  const [tab, setTab] = useState<Tab>('Feature status');
  const [domain, setDomain] = useState('All domains');
  const domains = useMemo(() => ['All domains', ...Array.from(new Set(featureRegister.map(item => item.domain)))], []);
  const visible = useMemo(() => domain === 'All domains' ? featureRegister : featureRegister.filter(item => item.domain === domain), [domain]);
  const counts = useMemo(() => {
    const total = featureRegister.length;
    const live = featureRegister.filter(item => item.status === 'live').length;
    const gated = featureRegister.filter(item => item.status === 'configured' || item.status === 'staged').length;
    const off = total - live - gated;
    return {
      total,
      live,
      gated,
      off
    };
  }, []);
  const checklist = useMemo(() => {
    const items = readinessGroups.flatMap(group => group.items);
    return {
      total: items.length,
      complete: items.filter(item => item.state === 'complete').length,
      blocked: items.filter(item => item.state === 'blocked').length
    };
  }, []);
  return <div className="space-y-6" data-id="element-7180" data-mp="w69fd0f7hkm8">
      <PageHeader eyebrow="Platform operations" title="Production readiness" description="What is genuinely live, what is built but deliberately not enabled, and what still blocks launch. Every other surface reads the same register, so nothing can claim to be operational here and be gated in the product." actions={<Badge tone="warn" data-id="element-7182" data-mp="df8kanh7i97y">Development environment</Badge>} data-id="element-7181" data-mp="r4ty7jg5fy94" />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-7183" data-mp="8eks3yh8rmc5">
        <Stat label="Capabilities registered" value={String(counts.total)} note="Each appears once." data-id="element-7184" data-mp="3frhcdtca9fo" />
        <Stat label="Live in production" value={String(counts.live)} note="Fully operational." tone="ok" data-id="element-7185" data-mp="3vjk01f465jqj" />
        <Stat label="Built but gated" value={String(counts.gated)} note="Awaiting a provider, approval or rollout." tone="warn" data-id="element-7186" data-mp="1xt8vxbbrnvyk" />
        <Stat label="Launch items blocked" value={String(checklist.blocked)} note={`${checklist.complete} of ${checklist.total} complete.`} tone="danger" data-id="element-7187" data-mp="20wf1ukze0b2i" />
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Production readiness sections" data-id="element-7188" data-mp="1ru5qxu07ifol" />

      {tab === 'Feature status' && <Panel title="Feature status register" description="The single source of truth for whether a capability is operational." action={<div className="w-full sm:w-64" data-id="element-7190" data-mp="1mx713nnksuw7">
              <Select value={domain} onChange={event => setDomain(event.target.value)} aria-label="Filter by domain" data-id="element-7191" data-mp="186zqfu3t8g62">
                {domains.map(item => <option key={item} value={item} data-id="element-7192" data-mp="z72ud7f3cf0z">
                    {item}
                  </option>)}
              </Select>
            </div>} bodyClassName="p-0 sm:p-0" data-id="element-7189" data-mp="1vy5545y0l2es">
          <ul className="divide-y divide-line" data-id="element-7193" data-mp="kjupeocdl5xg">
            {visible.map(feature => <li key={feature.key} className="px-5 py-4 sm:px-6" data-id="element-7194" data-mp="fljesbe9are6">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-7195" data-mp="tkiugtjsjvy7">
                  <div className="min-w-0" data-id="element-7196" data-mp="pinvvwt8vwo4">
                    <p className="text-body font-semibold text-ink" data-id="element-7197" data-mp="pgumi9r8yycf">{feature.label}</p>
                    <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-7198" data-mp="13gvx4gxbdk53">
                      {feature.domain} · {feature.surface}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2" data-id="element-7199" data-mp="1d89rkaaertrd">
                    <FeatureStatusBadge status={feature.status} data-id="element-7200" data-mp="188x53qng8opb" />
                    {feature.dependency !== 'none' && <Badge tone="neutral" plain data-id="element-7201" data-mp="1m854oknixyu1">
                        {dependencyLabel(feature.dependency)}
                      </Badge>}
                  </div>
                </div>
                <p className="mt-2 max-w-[80ch] text-body-sm text-ink" data-id="element-7202" data-mp="ewytwloc5a4y">{feature.note}</p>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Launch checklist' && <div className="space-y-5" data-id="element-7203" data-mp="nwls6ylrpzts">
          <Panel title="Overall" description="Derived from the lines below, not asserted." data-id="element-7204" data-mp="nvkk9301ttga">
            <Meter value={checklist.complete / checklist.total * 100} label="Launch items complete" trailing={`${checklist.complete} of ${checklist.total}`} data-id="element-7205" data-mp="sp5au9zbbl5g" />
            <p className="mt-3 max-w-[70ch] text-body-sm text-ink-muted" data-id="element-7206" data-mp="12nupzuztqri0">
              A blocked line is not a defect — it is a dependency outside engineering. Launch cannot
              proceed while any financial line remains blocked, because the interface would then be
              implying a capability the platform is not permitted to operate.
            </p>
          </Panel>

          {readinessGroups.map(group => <Panel key={group.label} title={group.label} description={group.description} data-id="element-7207" data-mp="35u0kl0tglj9n">
              <ul className="space-y-3" data-id="element-7208" data-mp="313tv6yr4huqc">
                {group.items.map(item => {
            const meta = STATE_META[item.state];
            const Icon = meta.icon;
            return <li key={item.label} className="flex items-start gap-3" data-id="element-7209" data-mp="2s3f75rmry1j5">
                      <Icon aria-hidden className={`mt-0.5 h-5 w-5 shrink-0 ${meta.className}`} data-id="element-7210" data-mp="3ot60hnjbxind" />
                      <div className="min-w-0" data-id="element-7211" data-mp="3juur1dwy5a5o">
                        <p className="text-body font-medium text-ink" data-id="element-7212" data-mp="3fryfg75tqeb8">
                          {item.label}
                          <span className="ml-2 text-body-sm font-normal text-ink-muted" data-id="element-7213" data-mp="3au6bgerwn1qo">
                            {meta.label}
                          </span>
                        </p>
                        <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-7214" data-mp="1zdorhe5hurkx">{item.note}</p>
                      </div>
                    </li>;
          })}
              </ul>
            </Panel>)}
        </div>}

      {tab === 'Gap audit' && <Panel title="Where the product actually stands" description="Classified before any further feature work, as the transition specification requires." data-id="element-7215" data-mp="3pi9xrmcuic9k">
          <ul className="space-y-4" data-id="element-7216" data-mp="3uj7lsfkje0iz">
            {gapAudit.map(gap => <li key={gap.area} className="rounded-card border border-line bg-canvas p-4" data-id="element-7217" data-mp="m2ct1sxgdwim">
                <Badge tone={gap.classification === 'complete' ? 'ok' : gap.classification === 'needs_approval' ? 'danger' : 'warn'} data-id="element-7218" data-mp="r04i8f6pr4j4">
                  {GAP_LABELS[gap.classification]}
                </Badge>
                <p className="mt-2.5 text-body font-medium text-ink" data-id="element-7219" data-mp="19s74c624jd0r">{gap.area}</p>
                <p className="mt-1 max-w-[80ch] text-body-sm text-ink-muted" data-id="element-7220" data-mp="2ugreg4m8qolo">{gap.detail}</p>
              </li>)}
          </ul>
        </Panel>}

      {tab === 'Environments' && <div className="space-y-5" data-id="element-7221" data-mp="33a4fjtugkwiu">
          <Panel title="Environment separation" description="Production data is never copied into development." data-id="element-7222" data-mp="339cs56b0m53n">
            <ul className="divide-y divide-line" data-id="element-7223" data-mp="2bif78oew3osq">
              {[{
            name: 'Development',
            data: 'Seeded records, labelled as development data everywhere they appear.',
            finance: 'All movement of value disabled.'
          }, {
            name: 'Staging',
            data: 'Seeded records plus provider sandbox references.',
            finance: 'Sandbox providers only. No real settlement.'
          }, {
            name: 'Production',
            data: 'Administrator-entered and user-generated content only. No seed data.',
            finance: 'Enabled per capability, only after the compliance gate is cleared.'
          }].map(env => <li key={env.name} className="py-4 first:pt-0 last:pb-0" data-id="element-7224" data-mp="fmlc0zkdhw1r">
                  <p className="text-body font-semibold text-ink" data-id="element-7225" data-mp="bmkx7dtd3iu6">{env.name}</p>
                  <p className="mt-1 text-body-sm text-ink-muted" data-id="element-7226" data-mp="6myjl39mg0ku">{env.data}</p>
                  <p className="mt-1 text-body-sm text-ink-muted" data-id="element-7227" data-mp="6kvj1pauep1v">{env.finance}</p>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Financial correction policy" description="Applies in every environment, without exception." data-id="element-7228" data-mp="3stepukrqjrlu">
            <ul className="space-y-2.5 text-body-sm text-ink" data-id="element-7229" data-mp="3ouxrapmdrpg1">
              <li data-id="element-7230" data-mp="1r1ifhehb0nsn">A historical ledger entry is never edited, reordered or removed.</li>
              <li data-id="element-7231" data-mp="1n29k656m8huz">
                A correction is a new entry — reversal, adjustment or refund — that references the
                original and carries a reason and an actor.
              </li>
              <li data-id="element-7232" data-mp="39wwapwfxb09c">Balances are always derived from entries, never stored and patched.</li>
              <li data-id="element-7233" data-mp="3eoebokhy50ub">
                A webhook may be replayed safely because every financial write carries an
                idempotency key.
              </li>
              <li data-id="element-7234" data-mp="3is2j936kvwfk">
                A discrepancy is surfaced by reconciliation and resolved by a transaction, never by
                a database fix.
              </li>
            </ul>
          </Panel>
        </div>}
    </div>;
}
function Stat({
  label,
  value,
  note,
  tone
}: {
  label: string;
  value: string;
  note: string;
  tone?: 'ok' | 'warn' | 'danger';
}) {
  const valueClass = tone === 'ok' ? 'text-ok-700' : tone === 'warn' ? 'text-warn-700' : tone === 'danger' ? 'text-danger-700' : 'text-ink';
  return <div className="rounded-card border border-line bg-surface p-5 shadow-panel" data-id="element-7235" data-mp="f7oixmxq8sy3">
      <p className="text-body-sm text-ink-muted" data-id="element-7236" data-mp="2bb2w2dyumu80">{label}</p>
      <p className={`cz-num mt-1.5 text-h2 font-semibold ${valueClass}`} data-id="element-7237" data-mp="27bls5oz2m54y">{value}</p>
      <p className="mt-1 text-caption text-ink-muted" data-id="element-7238" data-mp="2latvmu29nzey">{note}</p>
    </div>;
}